import { Controller } from '@hotwired/stimulus'
import { patch } from '@rails/request.js'
import { track } from '../app/modules/analytics'

/**
 * BootstrapCollapsePersistence
 *
 * Remembers a Bootstrap collapse's open/closed state server-side. Listens to the
 * settled shown/hidden events on the collapse target and PATCHes a boolean to the
 * given URL. Pairs with bootstrap-collapse-chevron on the same element — that one
 * handles the icon, this one persists.
 *
 * Values:
 *   url — endpoint that accepts PATCH <param>[<key>]=true|false (optional; omit for tracking-only)
 *   param — the model param wrapper (e.g. "kpi_template")
 *   key — the boolean attribute name to persist
 *   saved — reflects the last value the server accepted ("true"/"false")
 *   openEvent / closeEvent — curated PostHog event names fired on open/close (optional)
 */
export default class extends Controller {
    static values = {
        url: String,
        param: String,
        key: String,
        saved: String,
        openEvent: String,
        closeEvent: String,
    }
    static targets = ['collapse']

    collapseTargetConnected(element) {
        this.shownHandler = (event) => this.#onToggle(event, element, true)
        this.hiddenHandler = (event) => this.#onToggle(event, element, false)
        element.addEventListener('shown.bs.collapse', this.shownHandler)
        element.addEventListener('hidden.bs.collapse', this.hiddenHandler)
    }

    collapseTargetDisconnected(element) {
        element.removeEventListener('shown.bs.collapse', this.shownHandler)
        element.removeEventListener('hidden.bs.collapse', this.hiddenHandler)
    }

    // Bootstrap collapse events bubble, so ignore any fired by a nested collapse.
    #onToggle(event, element, expanded) {
        if (event.target !== element) return
        this.#save(expanded)
        this.#trackPanel(expanded)
    }

    async #save(expanded) {
        if (!this.urlValue) return // tracking-only usage — nothing to persist

        const body = new FormData()
        body.append(`${this.paramValue}[${this.keyValue}]`, expanded)

        this.savedValue = ''
        const response = await patch(this.urlValue, { body, responseKind: 'turbo-stream' })
        if (response.ok) this.savedValue = String(expanded)
    }

    #trackPanel(expanded) {
        const name = expanded ? this.openEventValue : this.closeEventValue
        if (name) track(name) // track() no-ops when analytics is off
    }
}
